import {
  BUSINESS_ENTITY_TYPES,
  EQUIPMENT_CONDITIONS,
  EQUIPMENT_TITLE_KINDS,
  formatMoney,
  type PricingResult,
  SOFT_COST_KINDS,
} from '@tex/finance-shared'
import { Plus, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  type BuyerDraft,
  type DeliveryDraft,
  emptyEquipment,
  emptySoftCost,
  ENTITY_TYPE_LABELS,
  type EquipmentDraft,
  type PricingDraft,
  type PricingPreview,
  SOFT_COST_LABELS,
  US_STATES,
} from '../lib/case-form'
import { humanize } from '../lib/format'
import { Button, Field, Input, Mono, Select, Textarea } from './ui'

type Setter<T> = (update: (prev: T) => T) => void

export function StateSelect({ value, onChange, disabled, required }: { value: string; onChange: (v: string) => void; disabled?: boolean; required?: boolean }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} required={required}>
      <option value="">—</option>
      {US_STATES.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </Select>
  )
}

export function MoneyInput({
  value,
  onChange,
  disabled,
  required,
  placeholder = '0.00',
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  required?: boolean
  placeholder?: string
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-neutral-400">$</span>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode="decimal"
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className="pl-6 font-mono tabular-nums"
      />
    </div>
  )
}

function Grid({ children, cols = 2 }: { children: ReactNode; cols?: 2 | 3 | 4 }) {
  const c = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' }[cols]
  return <div className={`grid grid-cols-1 gap-3 ${c}`}>{children}</div>
}

// ── buyer ──

export function BuyerFields({ draft, set, disabled }: { draft: BuyerDraft; set: Setter<BuyerDraft>; disabled?: boolean }) {
  const field = <K extends keyof BuyerDraft>(k: K) => (v: BuyerDraft[K]) => set((d) => ({ ...d, [k]: v }))
  const addr = <K extends keyof BuyerDraft['address']>(k: K) => (v: string) => set((d) => ({ ...d, address: { ...d.address, [k]: v } }))
  return (
    <div className="space-y-3">
      <Grid>
        <Field label="Buyer business name">
          <Input value={draft.business_name} onChange={(e) => field('business_name')(e.target.value)} required disabled={disabled} />
        </Field>
        <Field label="Entity type">
          <Select value={draft.entity_type} onChange={(e) => field('entity_type')(e.target.value as BuyerDraft['entity_type'])} disabled={disabled}>
            <option value="">Not known yet</option>
            {BUSINESS_ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {ENTITY_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Contact name">
          <Input value={draft.contact_name} onChange={(e) => field('contact_name')(e.target.value)} required disabled={disabled} />
        </Field>
        <Field label="Contact email" hint="The buyer completes their own application from this address.">
          <Input type="email" value={draft.contact_email} onChange={(e) => field('contact_email')(e.target.value)} required disabled={disabled} />
        </Field>
        <Field label="Contact phone">
          <Input type="tel" value={draft.contact_phone} onChange={(e) => field('contact_phone')(e.target.value)} disabled={disabled} />
        </Field>
      </Grid>
      <Grid>
        <Field label="Buyer state" hint="Where the business is based.">
          <StateSelect value={draft.state} onChange={field('state')} disabled={disabled} />
        </Field>
        <Field label="Equipment use state" hint="Where the equipment will be used.">
          <StateSelect value={draft.equipment_use_state} onChange={field('equipment_use_state')} disabled={disabled} />
        </Field>
      </Grid>
      <fieldset className="rounded-md border border-[var(--border-subtle)] p-3">
        <legend className="px-1 text-xs font-medium uppercase tracking-wide text-neutral-500">Business address (optional)</legend>
        <div className="space-y-3">
          <Field label="Street">
            <Input value={draft.address.line1} onChange={(e) => addr('line1')(e.target.value)} disabled={disabled} />
          </Field>
          <Field label="Suite / line 2">
            <Input value={draft.address.line2} onChange={(e) => addr('line2')(e.target.value)} disabled={disabled} />
          </Field>
          <Grid cols={3}>
            <Field label="City">
              <Input value={draft.address.city} onChange={(e) => addr('city')(e.target.value)} disabled={disabled} />
            </Field>
            <Field label="State">
              <StateSelect value={draft.address.state} onChange={addr('state')} disabled={disabled} />
            </Field>
            <Field label="ZIP">
              <Input value={draft.address.postal_code} onChange={(e) => addr('postal_code')(e.target.value)} inputMode="numeric" disabled={disabled} />
            </Field>
          </Grid>
        </div>
      </fieldset>
    </div>
  )
}

// ── equipment ──

export function EquipmentEditor({ lines, set, disabled }: { lines: EquipmentDraft[]; set: Setter<EquipmentDraft[]>; disabled?: boolean }) {
  const update = (key: string, patch: Partial<EquipmentDraft>) => set((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  const remove = (key: string) => set((ls) => ls.filter((l) => l.key !== key))
  return (
    <div className="space-y-3">
      {lines.map((l, i) => (
        <fieldset key={l.key} className="rounded-md border border-[var(--border-subtle)] p-3">
          <legend className="flex items-center gap-2 px-1 text-xs font-medium uppercase tracking-wide text-neutral-500">
            Line <Mono>{i + 1}</Mono>
          </legend>
          <div className="space-y-3">
            <Grid cols={4}>
              <Field label="Category" hint="e.g. excavator, skid steer, dump truck">
                <Input value={l.category} onChange={(e) => update(l.key, { category: e.target.value })} required disabled={disabled} />
              </Field>
              <Field label="Make">
                <Input value={l.make} onChange={(e) => update(l.key, { make: e.target.value })} disabled={disabled} />
              </Field>
              <Field label="Model">
                <Input value={l.model} onChange={(e) => update(l.key, { model: e.target.value })} disabled={disabled} />
              </Field>
              <Field label="Model year">
                <Input value={l.model_year} onChange={(e) => update(l.key, { model_year: e.target.value })} inputMode="numeric" className="font-mono" disabled={disabled} />
              </Field>
            </Grid>
            <Grid cols={4}>
              <Field label="Condition">
                <Select value={l.condition} onChange={(e) => update(l.key, { condition: e.target.value as EquipmentDraft['condition'] })} disabled={disabled}>
                  {EQUIPMENT_CONDITIONS.map((c) => (
                    <option key={c} value={c}>
                      {humanize(c)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Quantity">
                <Input value={l.quantity} onChange={(e) => update(l.key, { quantity: e.target.value })} inputMode="numeric" required className="font-mono" disabled={disabled} />
              </Field>
              <Field label="Unit price">
                <MoneyInput value={l.unit_price} onChange={(v) => update(l.key, { unit_price: v })} required disabled={disabled} />
              </Field>
              <Field label="Hours / miles">
                <Input value={l.hours_or_miles} onChange={(e) => update(l.key, { hours_or_miles: e.target.value })} inputMode="numeric" className="font-mono" disabled={disabled} />
              </Field>
            </Grid>
            <Grid cols={4}>
              <Field label="Serial / VIN">
                <Input value={l.serial_number} onChange={(e) => update(l.key, { serial_number: e.target.value })} className="font-mono" disabled={disabled} />
              </Field>
              <Field label="Title">
                <Select
                  value={l.title_kind}
                  onChange={(e) => {
                    const title_kind = e.target.value as EquipmentDraft['title_kind']
                    update(l.key, { title_kind, vehicle_class: title_kind === 'vehicle' ? l.vehicle_class : '' })
                  }}
                  disabled={disabled}
                >
                  <option value="">Not known yet</option>
                  {EQUIPMENT_TITLE_KINDS.map((t) => (
                    <option key={t} value={t}>
                      {humanize(t)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Vehicle class" hint="Only for titled vehicles.">
                <Input value={l.vehicle_class} onChange={(e) => update(l.key, { vehicle_class: e.target.value })} disabled={disabled || l.title_kind !== 'vehicle'} />
              </Field>
              <Field label="Listing URL">
                <Input type="url" value={l.listing_url} onChange={(e) => update(l.key, { listing_url: e.target.value })} disabled={disabled} />
              </Field>
            </Grid>
            <Field label="Description">
              <Textarea rows={2} value={l.description} onChange={(e) => update(l.key, { description: e.target.value })} disabled={disabled} />
            </Field>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input type="checkbox" checked={l.existing_lien_noted} onChange={(e) => update(l.key, { existing_lien_noted: e.target.checked })} disabled={disabled} />
                Existing lien on this unit
              </label>
              <Button type="button" variant="danger" onClick={() => remove(l.key)} disabled={disabled || lines.length === 1}>
                <Trash2 className="mr-1 h-4 w-4" aria-hidden /> Remove line
              </Button>
            </div>
          </div>
        </fieldset>
      ))}
      <Button type="button" variant="secondary" onClick={() => set((ls) => [...ls, emptyEquipment()])} disabled={disabled || lines.length >= 50}>
        <Plus className="mr-1 h-4 w-4" aria-hidden /> Add equipment line
      </Button>
    </div>
  )
}

// ── pricing ──

export function PricingEditor({ draft, set, disabled }: { draft: PricingDraft; set: Setter<PricingDraft>; disabled?: boolean }) {
  const field = <K extends keyof PricingDraft>(k: K) => (v: PricingDraft[K]) => set((d) => ({ ...d, [k]: v }))
  const updateCost = (key: string, patch: Partial<PricingDraft['soft_costs'][number]>) =>
    set((d) => ({ ...d, soft_costs: d.soft_costs.map((s) => (s.key === key ? { ...s, ...patch } : s)) }))
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">Soft costs financed with the equipment</div>
        {draft.soft_costs.length === 0 && <p className="text-sm text-neutral-500">None yet.</p>}
        {draft.soft_costs.map((s) => (
          <div key={s.key} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_2fr_1fr_auto] sm:items-end">
            <Field label="Kind">
              <Select value={s.kind} onChange={(e) => updateCost(s.key, { kind: e.target.value as PricingDraft['soft_costs'][number]['kind'] })} disabled={disabled}>
                {SOFT_COST_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {SOFT_COST_LABELS[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Label">
              <Input value={s.label} onChange={(e) => updateCost(s.key, { label: e.target.value })} disabled={disabled} />
            </Field>
            <Field label="Amount">
              <MoneyInput value={s.amount} onChange={(v) => updateCost(s.key, { amount: v })} required disabled={disabled} />
            </Field>
            <Button
              type="button"
              variant="danger"
              aria-label="Remove soft cost"
              onClick={() => set((d) => ({ ...d, soft_costs: d.soft_costs.filter((x) => x.key !== s.key) }))}
              disabled={disabled}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="secondary"
          onClick={() => set((d) => ({ ...d, soft_costs: [...d.soft_costs, emptySoftCost()] }))}
          disabled={disabled || draft.soft_costs.length >= 20}
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden /> Add soft cost
        </Button>
      </div>

      <Grid>
        <Field label="Discount" hint="Taken off equipment plus soft costs.">
          <MoneyInput value={draft.discount} onChange={field('discount')} disabled={disabled} />
        </Field>
        <Field label="Cash contribution" hint="Down payment the buyer brings.">
          <MoneyInput value={draft.cash_contribution} onChange={field('cash_contribution')} disabled={disabled} />
        </Field>
      </Grid>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input type="checkbox" checked={draft.trade_in_noted} onChange={(e) => field('trade_in_noted')(e.target.checked)} disabled={disabled} />
          Buyer has a trade-in
        </label>
        {draft.trade_in_noted && (
          <p className="text-xs text-neutral-500">Trade-ins do not change the invoice math here; a Tex specialist will review how it applies.</p>
        )}
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={draft.override_principal}
            onChange={(e) => set((d) => ({ ...d, override_principal: e.target.checked, requested_principal: '', reconciliation_reason: '' }))}
            disabled={disabled}
          />
          Request a different amount than invoice minus cash
        </label>
        {draft.override_principal && (
          <Grid>
            <Field label="Requested amount">
              <MoneyInput value={draft.requested_principal} onChange={field('requested_principal')} required disabled={disabled} />
            </Field>
            <Field label="Why it differs" hint="Required when the requested amount is overridden.">
              <Textarea rows={2} value={draft.reconciliation_reason} onChange={(e) => field('reconciliation_reason')(e.target.value)} required disabled={disabled} />
            </Field>
          </Grid>
        )}
      </div>
    </div>
  )
}

export function PricingSummary({ preview }: { preview: PricingPreview }) {
  if (!preview.ok) return <p className="text-sm text-neutral-500">{preview.message}</p>
  const r: PricingResult = preview.result
  const line = (label: string, amount: string, strong = false) => (
    <div className={`flex justify-between gap-4 py-1 text-sm ${strong ? 'font-semibold text-neutral-900' : 'text-neutral-700'}`}>
      <span>{label}</span>
      <Mono>{amount}</Mono>
    </div>
  )
  return (
    <div>
      {line('Equipment', formatMoney(r.equipmentTotal))}
      {line('Soft costs', formatMoney(r.financedSoftCosts))}
      {line('Discount', `−${formatMoney(r.discount)}`)}
      <div className="my-1 border-t border-[var(--border-subtle)]" />
      {line('Invoice total', formatMoney(r.invoiceTotal), true)}
      {line('Cash contribution', `−${formatMoney(r.cashContribution)}`)}
      <div className="my-1 border-t border-[var(--border-subtle)]" />
      {line('Requested financing', formatMoney(r.requestedPrincipal), true)}
      {r.reconciliationReason && <p className="mt-2 text-xs text-neutral-500">Override: {r.reconciliationReason}</p>}
      <p className="mt-2 text-xs text-neutral-500">Preview only — the saved request shows the totals Tex recorded.</p>
    </div>
  )
}

// ── delivery ──

export function DeliveryFields({ draft, set, disabled }: { draft: DeliveryDraft; set: Setter<DeliveryDraft>; disabled?: boolean }) {
  return (
    <Grid>
      <Field label="Expected delivery">
        <Input type="date" value={draft.expected_on} onChange={(e) => set((d) => ({ ...d, expected_on: e.target.value }))} className="font-mono" disabled={disabled} />
      </Field>
      <label className="flex items-center gap-2 pt-6 text-sm text-neutral-700">
        <input
          type="checkbox"
          checked={draft.pre_delivery_payment_requested}
          onChange={(e) => set((d) => ({ ...d, pre_delivery_payment_requested: e.target.checked }))}
          disabled={disabled}
        />
        Dealer needs payment before delivery
      </label>
    </Grid>
  )
}
